<template>
  <q-page class="bg-light q-pa-xl">
    <div class="container-md">
      <q-card class="q-pa-xl shadow-1 rounded-2xl bg-white">
        <!-- Заглавие -->
        <div class="header-row q-mb-md">
          <div class="header-title text-h6 text-mint text-bold">
            Поднеси ново барање
          </div>

          <q-btn
            flat
            dense
            round
            icon="close"
            class="btn-close"
            @click="$router.push('/klient/moite-baranja')"
          />
        </div>

        <!-- Инфо лента -->
        <div class="row q-col-gutter-md q-mb-lg text-grey-8">
          <div class="col-12 col-sm-6">
            <div class="bg-grey-2 rounded q-pa-sm flex items-center">
              <q-icon name="event" color="mint" class="q-mr-xs" size="18px" />
              <span>Датум:</span><b class="q-ml-xs">{{ todayDate }}</b>
            </div>
          </div>

          <div class="col-12 col-sm-6">
            <div class="bg-grey-2 rounded q-pa-sm flex items-center">
              <q-icon name="business" color="mint" class="q-mr-xs" size="18px" />
              <span>Фирма:</span><b class="q-ml-xs">{{ firmaName }}</b>
            </div>
          </div>
        </div>

        <!-- Форма -->
        <q-form ref="formRef" @submit.prevent="onSubmit" class="q-gutter-md">
          <div class="row q-col-gutter-xl">
            <!-- Лево -->
            <div class="col-12 col-md-6">
              <q-input
                v-model.trim="form.baratel"
                outlined
                dense
                color="mint"
                class="equal-input q-mb-md"
                placeholder="Име и Презиме"
                lazy-rules="ondemand"
                :rules="[(v) => !!v || 'Внесете го името на барателот']"
                label-slot
              >
                <template v-slot:label>
                  Барател <span class="required-star">*</span>
                </template>

                <template v-slot:prepend>
                  <q-icon name="person" color="mint" />
                </template>
              </q-input>

              <q-input
                v-model.trim="form.opis"
                type="textarea"
                outlined
                dense
                autogrow
                color="mint"
                class="equal-input q-mb-xs"
                placeholder="Опишете го проблемот детално..."
                lazy-rules="ondemand"
                :maxlength="maxOpisLength"
                :rules="[
                  (v) => !!v || 'Внесете опис',
                  (v) =>
                    (v && v.trim().length <= maxOpisLength) ||
                    `Описот смее да има најмногу ${maxOpisLength} знаци`,
                ]"
                label-slot
              >
                <template v-slot:label>
                  Опис на барањето <span class="required-star">*</span>
                </template>

                <template v-slot:prepend>
                  <q-icon name="description" color="mint" />
                </template>
              </q-input>

              <div class="opis-help-row q-mb-md">
                <div class="opis-help-text">
                  Описот смее да има најмногу {{ maxOpisLength }} знаци.
                </div>
                <div class="opis-counter">
                  {{ opisLength }}/{{ maxOpisLength }}
                </div>
              </div>

              <!-- Прикачи повеќе слики/фајлови -->
              <div class="upload-block">
                <div
                  class="white-upload upload-like-field flex flex-center cursor-pointer"
                  @click="selectFiles"
                >
                  <div class="text-grey-7 text-caption text-center">
                    {{
                      files.length
                        ? `${files.length} избрани фајлови`
                        : "Изберете фајлови од уредот"
                    }}
                  </div>

                  <input
                    ref="fileInput"
                    type="file"
                    accept="*/*"
                    multiple
                    class="hidden-input"
                    @change="handleFileUpload"
                  />
                </div>

                <div class="text-caption text-grey-7 q-mt-xs flex items-center">
                  <q-icon name="attach_file" color="mint" size="16px" class="q-mr-xs" />
                  Прикачи повеќе слики / фајлови
                </div>
              </div>
            </div>

            <!-- Десно -->
            <div class="col-12 col-md-6">
              <q-select
                v-model="form.stepen"
                :options="stepenOptions"
                outlined
                dense
                color="mint"
                class="equal-input q-mb-md"
                lazy-rules="ondemand"
                :rules="[(v) => !!v || 'Изберете степен на итност']"
                emit-value
                map-options
                label-slot
              >
                <template v-slot:label>
                  Степен на итност (1 – 5) <span class="required-star">*</span>
                </template>

                <template v-slot:prepend>
                  <q-icon name="whatshot" color="mint" />
                </template>
              </q-select>

              <q-select
                v-model="form.aplikacija"
                :options="aplikaciiOptions"
                option-label="Opis"
                option-value="SifraID"
                outlined
                dense
                color="mint"
                class="equal-input q-mb-md"
                emit-value
                map-options
                :loading="loadingAplikacii"
                lazy-rules="ondemand"
                :rules="[(v) => !!v || 'Изберете апликација']"
                label-slot
              >
                <template v-slot:label>
                  Апликација <span class="required-star">*</span>
                </template>

                <template v-slot:prepend>
                  <q-icon name="apps" color="mint" />
                </template>
              </q-select>

              <q-select
                v-model="form.oddel"
                :options="oddelOptions"
                outlined
                dense
                color="mint"
                class="equal-input"
                emit-value
                map-options
                lazy-rules="ondemand"
                :rules="[(v) => !!v || 'Изберете оддел за поддршка']"
                label-slot
              >
                <template v-slot:label>
                  Оддел за поддршка <span class="required-star">*</span>
                </template>

                <template v-slot:prepend>
                  <q-icon name="groups" color="mint" />
                </template>
              </q-select>
            </div>
          </div>

          <!-- Табела за прикачени фајлови -->
          <div v-if="files.length" class="q-mt-md">
            <q-table
              :data="files"
              :columns="fileColumns"
              row-key="id"
              flat
              bordered
              dense
              hide-bottom
              separator="horizontal"
            >
              <template v-slot:body-cell-preview="props">
                <q-td align="center">
                  <q-img
                    v-if="props.row.isImage"
                    :src="props.row.url"
                    style="width: 36px; height: 36px; border-radius: 6px"
                    contain
                  />
                  <q-icon v-else name="insert_drive_file" color="mint" />
                </q-td>
              </template>

              <template v-slot:body-cell-actions="props">
                <q-td align="center">
                  <q-btn
                    flat
                    dense
                    round
                    size="sm"
                    icon="delete"
                    color="negative"
                    @click="removeFile(props.row)"
                  >
                    <q-tooltip>Отстрани</q-tooltip>
                  </q-btn>
                </q-td>
              </template>
            </q-table>
          </div>

          <!-- Копче -->
          <div class="flex justify-center q-mt-xl">
            <q-btn
              label="Сними"
              icon="save"
              type="submit"
              color="mint"
              unelevated
              class="equal-btn text-white"
            />
          </div>
        </q-form>
      </q-card>

      <!-- Дијалог за потврда -->
      <q-dialog v-model="showConfirm">
        <q-card class="text-center q-pa-lg rounded-2xl">
          <q-card-section>
            <q-icon name="check_circle" color="mint" size="64px" />
            <div class="text-h6 text-mint q-mt-sm">
              Барањето е успешно поднесено!
            </div>
            <div class="text-caption q-mt-xs">
              Број: <b>{{ lastCreated && lastCreated.broj }}</b>
            </div>
          </q-card-section>

          <q-card-actions align="center">
            <q-btn flat label="Во ред" color="mint" @click="closeDialog" />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script>
import { getAplikacii } from "@/services/aplikaciiService";
import { dodadiBaranje } from "@/services/baranjaService";
import { getUser } from "@/services/db";

export default {
  name: "NovoBaranje",

  data() {
    return {
      user: getUser(),
      maxOpisLength: 1000,

      form: {
        baratel: "",
        opis: "",
        stepen: null,
        aplikacija: null,
        oddel: null,
      },

      files: [],

      stepenOptions: [
        { label: "1 - Најнизок", value: 1 },
        { label: "2 - Низок", value: 2 },
        { label: "3 - Среден", value: 3 },
        { label: "4 - Висок", value: 4 },
        { label: "5 - Најитен", value: 5 },
      ],

      oddelOptions: [
        { label: "Сервис", value: "servis" },
        { label: "Програмери", value: "programeri" },
        { label: "Менаџмент", value: "menadzment" },
      ],

      aplikaciiOptions: [],
      loadingAplikacii: false,
      showConfirm: false,
      lastCreated: null,

      fileColumns: [
        { name: "preview", label: "", align: "center" },
        { name: "name", label: "Име на фајл", field: "name", align: "left" },
        {
          name: "size",
          label: "Големина (KB)",
          field: "sizeKB",
          align: "center",
          format: (val) => (val != null ? val.toFixed(1) : ""),
        },
        { name: "actions", label: "Акции", align: "center" },
      ],
    };
  },

  computed: {
    firmaName() {
      const u = this.user || {};

      return (
        u.Firma ||
        u.firma ||
        u.NazivFirma ||
        u.nazivFirma ||
        u.NazivPartner ||
        u.nazivPartner ||
        u.PartnerNaziv ||
        u.partnerNaziv ||
        u.Company ||
        u.company ||
        "-"
      );
    },

    emailValue() {
      const u = this.user || {};

      return (
        u.Email ||
        u.email ||
        u.EMail ||
        u.eMail ||
        u.Eposta ||
        u.eposta ||
        u.Mail ||
        u.mail ||
        "-"
      );
    },

    todayDate() {
      const d = new Date();
      return `${String(d.getDate()).padStart(2, "0")}.${String(
        d.getMonth() + 1
      ).padStart(2, "0")}.${d.getFullYear()}`;
    },

    opisLength() {
      return (this.form.opis || "").length;
    },
  },

  methods: {
    async loadAplikacii() {
      this.loadingAplikacii = true;

      try {
        this.aplikaciiOptions = await getAplikacii();
      } catch (err) {
        this.$q.notify({
          color: "negative",
          message: "Не може да се вчитаат апликациите.",
          icon: "error",
        });
      } finally {
        this.loadingAplikacii = false;
      }
    },

    selectFiles() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    async handleFileUpload(e) {
      const picked = Array.from(e.target.files || []);

      if (!picked.length) return;

      const mapped = await Promise.all(
        picked.map((file) => this.convertToBase64(file))
      );

      this.files = (this.files || []).concat(mapped);
      e.target.value = "";
    },

    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const result = String(reader.result || "");
          const base64 = result.includes(",") ? result.split(",")[1] : result;

          resolve({
            id: `${file.name}-${file.size}-${Date.now()}`,
            base64: base64,
            name: file.name,
            size: file.size,
            sizeKB: file.size / 1024,
            type: file.type,
            isImage: (file.type || "").startsWith("image/"),
            url: URL.createObjectURL(file),
          });
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    removeFile(row) {
      if (row && row.url) {
        URL.revokeObjectURL(row.url);
      }

      this.files = this.files.filter((f) => f.id !== row.id);
    },

    async onSubmit() {
      const ok = await this.$refs.formRef.validate();

      if (!ok) {
        this.$q.notify({
          color: "warning",
          message: "Внесете ги сите задолжителни полиња.",
          icon: "warning",
        });
        return;
      }

      if ((this.form.opis || "").trim().length > this.maxOpisLength) {
        this.$q.notify({
          color: "warning",
          message: `Описот смее да има најмногу ${this.maxOpisLength} знаци.`,
          icon: "warning",
        });
        return;
      }

      try {
        const selectedAplikacija = this.aplikaciiOptions.find(
          (a) => String(a.SifraID) === String(this.form.aplikacija)
        );

        const selectedStepen = this.stepenOptions.find(
          (s) => Number(s.value) === Number(this.form.stepen)
        );

        const aplikacijaOpis = selectedAplikacija
          ? selectedAplikacija.Opis
          : "-";

        const prioritetText = selectedStepen
          ? selectedStepen.label.replace(/^\d+\s*-\s*/, "")
          : "-";

        const request = {
          // Оригинални полиња за постоечки frontend
          baratel: this.form.baratel,
          opis: this.form.opis,
          stepen: Number(this.form.stepen),
          aplikacija: Number(this.form.aplikacija),
          aplikacijaOpis: aplikacijaOpis,
          oddel: this.form.oddel,
          firma: this.firmaName,
          email: this.emailValue,
          prioritet: prioritetText,

          // Полиња што backend mail-от ги чита
          Baratel: this.form.baratel,
          Opis: this.form.opis,
          StepenItnost: Number(this.form.stepen),
          AplikacijaID: Number(this.form.aplikacija),
          AplikacijaOpis: aplikacijaOpis,
          Firma: this.firmaName,
          Email: this.emailValue,
          Prioritet: prioritetText,
          Oddel: this.form.oddel,

          sliki: this.files.map((f) => ({
            Fajl: f.base64,
            FileName: f.name,
          })),
        };

        console.log("request", request);

        // ВАЖНО:
        // Ова е единственото праќање од оваа компонента.
        // Во оваа компонента НЕ смее да има api.post("/baranja/add").
        const created = await dodadiBaranje(request);

        this.lastCreated = created;
        this.showConfirm = true;

        this.form = {
          baratel: "",
          opis: "",
          stepen: null,
          aplikacija: null,
          oddel: null,
        };

        this.files.forEach((f) => {
          if (f && f.url) URL.revokeObjectURL(f.url);
        });
        this.files = [];

        this.$nextTick(() => {
          this.$refs.formRef.resetValidation();
        });
      } catch (e) {
        console.error("Грешка при снимање:", e);

        this.$q.notify({
          color: "negative",
          message: e.message || "Настана грешка при снимање на барањето.",
          icon: "error",
        });
      }
    },

    closeDialog() {
      this.showConfirm = false;
      this.$router.push("/klient/moite-baranja");
    },
  },

  mounted() {
    this.loadAplikacii();
  },

  beforeDestroy() {
    (this.files || []).forEach((r) => {
      if (r && r.url) URL.revokeObjectURL(r.url);
    });
  },
};
</script>

<style scoped>
/* ===== Основни полиња ===== */
.equal-input {
  width: 100%;
  min-height: 46px;
}

.opis-help-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.opis-help-text {
  flex: 1;
}

.opis-counter {
  font-weight: 700;
  color: #2f6f63;
  white-space: nowrap;
}

/* ===== Upload зона ===== */
.white-upload {
  border: 2px dashed #cfcfcf;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.white-upload:hover {
  border-color: #2f6f63;
  background: #f7fdf9;
}

.upload-block {
  margin-top: -15px;
}

.upload-like-field {
  width: 100%;
  height: 46px;
  min-height: 46px;
  padding: 0 12px !important;
}

/* ===== Копчиња ===== */
.equal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  height: 44px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  padding: 0 24px;
  text-align: center;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.25s ease;
  letter-spacing: 0.3px;
}

/* Потемна зелена копче */
.equal-btn.mint,
.q-btn.equal-btn[color="mint"],
.equal-btn[type="submit"] {
  background-color: #2f6f63 !important;
  color: #ffffff !important;
  border: none;
}

.equal-btn.mint:hover,
.q-btn.equal-btn[color="mint"]:hover,
.equal-btn[type="submit"]:hover {
  background-color: #cadfdb !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Бело копче */
.equal-btn.grey,
.q-btn.equal-btn[color="grey-7"],
.q-btn.equal-btn[flat] {
  background-color: #ffffff !important;
  color: #2f6f63 !important;
  border: 1.8px solid #2f6f63 !important;
}

.equal-btn.grey:hover,
.q-btn.equal-btn[color="grey-7"]:hover,
.q-btn.equal-btn[flat]:hover {
  background-color: #f4f9f8 !important;
  box-shadow: 0 3px 8px rgba(47, 111, 99, 0.25);
  transform: translateY(-1px);
}

/* Responsive стилови */
@media (max-width: 600px) {
  .equal-btn {
    width: 100%;
    max-width: 300px;
    height: 46px;
    font-size: 14px;
  }

  .opis-help-row {
    flex-direction: column;
    gap: 4px;
  }

  .opis-counter {
    align-self: flex-end;
  }
}

.text-mint {
  color: #2f6f63;
}

.bg-light {
  background: #f6f8f7;
}

.hidden-input {
  display: none;
}

.header-row {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 44px;
}

.header-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: max-content;
}

.btn-close {
  margin-left: auto;
  border: 1.6px solid #2f6f63 !important;
  color: #2f6f63 !important;
  border-radius: 10px;
}

.btn-close:hover {
  background-color: #f4f9f8 !important;
  transform: none !important;
}

.required-star {
  color: #e53935;
  font-weight: 700;
  margin-left: 2px;
}
</style>
